import { useEffect } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'

import useExpensesData from '@/hooks/useExpensesData'
import ExpensesTable from './components/ExpensesTable'

import { useExpenseSelection } from './hooks/useExpenseSelection'
import { useExpenseActions } from './hooks/useExpenseActions'

import { useExpenseDateRangeFilter } from './filters/DateRangeFilter'
import { useExpenseTagFilter } from './filters/TagFilter'

export default function ExpensesList() {
	const { t } = useTranslation()
	const {
		data: expensesData,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData: loadExpensesData,
		handleLoadMore,
		setError,
		setSorting,
		handleSearch,
		sortColumn,
		sortDirection,
		searchQuery,
		updateFilters,
	} = useExpensesData()

	const { selectedExpenses, handleSelectAll, handleSelectExpense, clearSelection, getSelectionState } = useExpenseSelection()
	const { isDeleting, handleDeleteSelected } = useExpenseActions(loadExpensesData)

	const { selectedTags, handleTagFilter } = useExpenseTagFilter(updateFilters)
	const { fromDate, toDate, handleDateRangeFilter, getDateRangeDisplay } = useExpenseDateRangeFilter(updateFilters)

	useEffect(() => {
		if (error) {
			Swal.fire({ title: t('expenses.list.messages.errorTitle'), text: error, icon: 'error' }).then(() => setError())
		}
	}, [error, setError, t])

	useEffect(() => {
		loadExpensesData()
	}, [loadExpensesData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = event => {
		handleSearch(event.target.value)
	}

	const { isAllSelected, isIndeterminate } = getSelectionState(expensesData)

	const onDeleteSelected = () => {
		handleDeleteSelected(selectedExpenses, clearSelection)
	}

	const onSelectAll = checked => {
		handleSelectAll(checked, expensesData)
	}

	const buttons = [
		{
			type: 'delete',
			label: t('expenses.list.actions.delete'),
			count: selectedExpenses.size,
			onClick: onDeleteSelected,
			loading: isDeleting,
		},
		{
			type: 'date',
			label: t('expenses.list.actions.tags'),
			active: !!selectedTags.length,
			onClick: handleTagFilter,
			title: selectedTags.length
				? t('expenses.list.tagFilter.activeTitle', { tags: selectedTags.join(', ') })
				: t('expenses.list.tagFilter.buttonTitle'),
		},
		{
			type: 'date',
			label: t('expenses.list.actions.expenseDate'),
			active: !!(fromDate || toDate),
			onClick: handleDateRangeFilter,
			title: getDateRangeDisplay() || t('expenses.list.dateFilter.buttonTitle'),
		},
		{
			type: 'add',
			label: t('expenses.list.actions.addExpense'),
			to: '/ksiegowosc/expenses/add',
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder={t('expenses.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<ExpensesTable
				expensesData={expensesData}
				loading={loading}
				selectedExpenses={selectedExpenses}
				onSelectAll={onSelectAll}
				onSelectExpense={handleSelectExpense}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={expensesData.length}
				totalCount={totalCount}
			/>
		</>
	)
}
